import React from "react";
import {
  Shield,
  ShieldCheck,
  Lock,
  Clock,
  Laptop,
  CreditCard,
  UserCheck,
  Check,
  Sliders,
  Sparkles,
  AlertTriangle,
  Info
} from "lucide-react";

export interface BatchPrivacySectionProps {
  batchActionType: any;
  setBatchActionType: (updater: any) => void;
  batchTwoFaMode: "set" | "clear";
  setBatchTwoFaMode: (mode: "set" | "clear") => void;
  batchTwoFaPassword: string;
  setBatchTwoFaPassword: (v: string) => void;
  batchTwoFaHint: string;
  setBatchTwoFaHint: (v: string) => void;
  batchTwoFaCurrentPassword: string;
  setBatchTwoFaCurrentPassword: (v: string) => void;
  batchPrivacyRules: Record<string, "all" | "contacts" | "nobody" | "keep">;
  setBatchPrivacyRules: React.Dispatch<
    React.SetStateAction<Record<string, "all" | "contacts" | "nobody" | "keep">>
  >;
  batchAccountTTL: number;
  setBatchAccountTTL: (v: number) => void;
  batchAutoDeleteTTL: number;
  setBatchAutoDeleteTTL: (v: number) => void;
  batchSuggestTopContacts: boolean;
  setBatchSuggestTopContacts: (v: boolean) => void;
  batchTerminateOtherSessions: boolean;
  setBatchTerminateOtherSessions: (v: boolean) => void;
  batchClearPayments: boolean;
  setBatchClearPayments: (v: boolean) => void;
}

export const BatchPrivacySection: React.FC<BatchPrivacySectionProps> = ({
  batchActionType,
  setBatchActionType,
  batchTwoFaMode,
  setBatchTwoFaMode,
  batchTwoFaPassword,
  setBatchTwoFaPassword,
  batchTwoFaHint,
  setBatchTwoFaHint,
  batchTwoFaCurrentPassword,
  setBatchTwoFaCurrentPassword,
  batchPrivacyRules,
  setBatchPrivacyRules,
  batchAccountTTL,
  setBatchAccountTTL,
  batchAutoDeleteTTL,
  setBatchAutoDeleteTTL,
  batchSuggestTopContacts,
  setBatchSuggestTopContacts,
  batchTerminateOtherSessions,
  setBatchTerminateOtherSessions,
  batchClearPayments,
  setBatchClearPayments
}) => {
  // Preset handler
  const applyPreset = (type: "strict" | "public" | "contacts") => {
    if (type === "strict") {
      setBatchPrivacyRules({
        phoneNumber: "nobody",
        lastSeen: "nobody",
        profilePhoto: "all",
        forwards: "nobody",
        phoneCalls: "nobody",
        voiceMessages: "nobody",
        chatInvites: "contacts",
        bio: "all",
        birthday: "nobody",
        gifts: "nobody",
        messages: "all",
        savedMusic: "nobody"
      });
    } else if (type === "public") {
      setBatchPrivacyRules({
        phoneNumber: "all",
        lastSeen: "all",
        profilePhoto: "all",
        forwards: "all",
        phoneCalls: "all",
        voiceMessages: "all",
        chatInvites: "all",
        bio: "all",
        birthday: "all",
        gifts: "all",
        messages: "all",
        savedMusic: "all"
      });
    } else if (type === "contacts") {
      setBatchPrivacyRules({
        phoneNumber: "contacts",
        lastSeen: "contacts",
        profilePhoto: "contacts",
        forwards: "contacts",
        phoneCalls: "contacts",
        voiceMessages: "contacts",
        chatInvites: "contacts",
        bio: "contacts",
        birthday: "contacts",
        gifts: "contacts",
        messages: "contacts",
        savedMusic: "contacts"
      });
    }
  };

  const privacyItems: { key: string; label: string; desc: string }[] = [
    { key: "phoneNumber", label: "电话号码", desc: "控制谁可以通过手机号搜到/看到此账号" },
    { key: "lastSeen", label: "最后上线时间", desc: "控制谁能看到最后在线状态与活跃时间" },
    { key: "profilePhoto", label: "个人头像", desc: "控制谁可以看到此账号的头像" },
    { key: "forwards", label: "转发消息来源", desc: "控制转发您消息时是否附带账号链接" },
    { key: "phoneCalls", label: "语音通话", desc: "控制谁可以直接向此账号发起语音/视频通话" },
    { key: "voiceMessages", label: "语音留言", desc: "控制谁可以向您发送语音消息" },
    { key: "chatInvites", label: "被邀请进群", desc: "控制谁可以把此账号直接拉入群聊或频道" },
    { key: "bio", label: "个人简介", desc: "控制谁可以查看账号个人简介 (Bio)" },
    { key: "birthday", label: "生日", desc: "控制谁可以查看个人生日信息" },
    { key: "gifts", label: "礼物", desc: "控制谁可以查看收到的礼物与星标" },
    { key: "messages", label: "消息", desc: "控制消息私聊权限" },
    { key: "savedMusic", label: "保存的音乐", desc: "控制谁可以看到您收藏的音乐列表" }
  ];

  return (
    <div className="space-y-4">
      {/* Section 1: 隐私权限策略 (Privacy Rules) */}
      <div className="p-4 bg-slate-950/60 border border-slate-800 rounded-xl space-y-3">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={!!batchActionType.privacyRules}
            onChange={(e) =>
              setBatchActionType({ ...batchActionType, privacyRules: e.target.checked })
            }
            className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 focus:ring-offset-slate-900"
          />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-400">
              <ShieldCheck className="w-3.5 h-3.5" />
            </div>
            <span className="text-xs font-bold text-slate-200">
              批量配置 Telegram 隐私权限策略 (推荐)
            </span>
          </div>
        </label>

        {batchActionType.privacyRules && (
          <div className="pl-6 pt-1 space-y-3">
            {/* Presets */}
            <div className="p-3 bg-purple-950/20 border border-purple-500/20 rounded-xl space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-purple-300 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                  一键应用快捷模版:
                </span>
                <span className="text-[10px] text-slate-400">点击自动填充下方规则</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => applyPreset("strict")}
                  className="px-2.5 py-1.5 rounded-lg bg-purple-900/40 hover:bg-purple-800/50 border border-purple-500/30 text-purple-200 text-xs font-medium flex items-center justify-center gap-1 transition"
                >
                  🛡️ 最强防封私密 (推荐)
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset("public")}
                  className="px-2.5 py-1.5 rounded-lg bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700 text-slate-300 text-xs font-medium flex items-center justify-center gap-1 transition"
                >
                  🌐 营销获客全公开
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset("contacts")}
                  className="px-2.5 py-1.5 rounded-lg bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700 text-slate-300 text-xs font-medium flex items-center justify-center gap-1 transition"
                >
                  🔒 仅联系人私密
                </button>
              </div>
            </div>

            {/* Granular Rules Table */}
            <div className="border border-slate-800 rounded-xl overflow-hidden divide-y divide-slate-800/60 text-xs bg-slate-900/50">
              <div className="px-3 py-2 bg-slate-950/40 grid grid-cols-12 items-center text-[11px] font-semibold text-slate-400">
                <div className="col-span-5 sm:col-span-4">权限项目</div>
                <div className="col-span-7 sm:col-span-8 text-right sm:text-center">
                  批量设置为
                </div>
              </div>

              {privacyItems.map((item) => {
                const currentVal = batchPrivacyRules[item.key] || "keep";
                return (
                  <div
                    key={item.key}
                    className="px-3 py-2.5 grid grid-cols-12 items-center gap-2 hover:bg-slate-800/30 transition-colors"
                  >
                    <div className="col-span-12 sm:col-span-5 flex flex-col">
                      <span className="font-semibold text-slate-200">{item.label}</span>
                      <span className="text-[10px] text-slate-400 leading-tight">
                        {item.desc}
                      </span>
                    </div>
                    <div className="col-span-12 sm:col-span-7 flex items-center justify-end sm:justify-center gap-1">
                      {(
                        [
                          { id: "all", label: "所有人", color: "text-emerald-300 bg-emerald-950/40 border-emerald-500/30" },
                          { id: "contacts", label: "联系人", color: "text-sky-300 bg-sky-950/40 border-sky-500/30" },
                          { id: "nobody", label: "无人", color: "text-rose-300 bg-rose-950/40 border-rose-500/30" },
                          { id: "keep", label: "保持原样", color: "text-slate-400 bg-slate-800/40 border-slate-700" }
                        ] as const
                      ).map((opt) => {
                        const isSelected = currentVal === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() =>
                              setBatchPrivacyRules((prev) => ({
                                ...prev,
                                [item.key]: opt.id
                              }))
                            }
                            className={`px-2 py-1 rounded-md text-[11px] font-medium border transition-all ${
                              isSelected
                                ? `${opt.color} ring-1 ring-purple-500 shadow-sm font-semibold`
                                : "bg-slate-900 border-slate-800 text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Section 2: 两步验证 (2FA Setup / Clear) */}
      <div className="p-4 bg-slate-950/60 border border-slate-800 rounded-xl space-y-3">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={!!batchActionType.twoFa}
            onChange={(e) =>
              setBatchActionType({ ...batchActionType, twoFa: e.target.checked })
            }
            className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 focus:ring-offset-slate-900"
          />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-400">
              <Lock className="w-3.5 h-3.5" />
            </div>
            <span className="text-xs font-bold text-slate-200">
              批量配置两步验证 (2FA) 密码
            </span>
          </div>
        </label>

        {batchActionType.twoFa && (
          <div className="pl-6 pt-1 space-y-3 text-xs">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1.5 cursor-pointer text-slate-300">
                <input
                  type="radio"
                  name="batchTwoFaMode"
                  value="set"
                  checked={batchTwoFaMode === "set"}
                  onChange={() => setBatchTwoFaMode("set")}
                  className="text-purple-600 bg-slate-900 border-slate-700"
                />
                <span>统一设置新密码</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-slate-300">
                <input
                  type="radio"
                  name="batchTwoFaMode"
                  value="clear"
                  checked={batchTwoFaMode === "clear"}
                  onChange={() => setBatchTwoFaMode("clear")}
                  className="text-purple-600 bg-slate-900 border-slate-700"
                />
                <span className="text-rose-400">一键清空/关闭 2FA</span>
              </label>
            </div>

            {batchTwoFaMode === "set" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-[11px] text-slate-400 mb-1">
                    新 2FA 密码: <span className="text-rose-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={batchTwoFaPassword}
                    onChange={(e) => setBatchTwoFaPassword(e.target.value)}
                    placeholder="输入统一的 2FA 密码"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-400 mb-1">
                    密码提示 (Hint, 可选):
                  </label>
                  <input
                    type="text"
                    value={batchTwoFaHint}
                    onChange={(e) => setBatchTwoFaHint(e.target.value)}
                    placeholder="例如: tg2026"
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-[11px] text-slate-400 mb-1">
                账号当前旧密码 (若选中的账号已有旧 2FA 密码，请在此填写):
              </label>
              <input
                type="text"
                value={batchTwoFaCurrentPassword}
                onChange={(e) => setBatchTwoFaCurrentPassword(e.target.value)}
                placeholder="如果原先无密码则留空"
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>
        )}
      </div>

      {/* Section 3: 账号保留期限 (TTL 自毁) */}
      <div className="p-4 bg-slate-950/60 border border-slate-800 rounded-xl space-y-3">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={!!batchActionType.accountTTL}
            onChange={(e) =>
              setBatchActionType({ ...batchActionType, accountTTL: e.target.checked })
            }
            className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 focus:ring-offset-slate-900"
          />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-400">
              <Clock className="w-3.5 h-3.5" />
            </div>
            <span className="text-xs font-bold text-slate-200">
              批量修改帐号保留期限 (离线自毁周期)
            </span>
          </div>
        </label>

        {batchActionType.accountTTL && (
          <div className="pl-6 pt-1 space-y-2 text-xs">
            <div className="flex items-center gap-3">
              <span className="text-slate-400 text-xs">离线后自动注销账号:</span>
              <select
                value={batchAccountTTL}
                onChange={(e) => setBatchAccountTTL(Number(e.target.value))}
                className="bg-slate-900 border border-slate-700 text-purple-300 font-semibold rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-purple-500"
              >
                <option value={30}>1 个月 (30 天)</option>
                <option value={90}>3 个月 (90 天)</option>
                <option value={182}>6 个月 (182 天)</option>
                <option value={365}>12 个月 (1 年)</option>
                <option value={548}>18 个月 (推荐)</option>
                <option value={730}>24 个月 (2 年)</option>
              </select>
            </div>
            <p className="text-[11px] text-slate-400">
              若在此期间账号未上线，Telegram 官方会将账号及所有群组消息彻底销毁。
            </p>
          </div>
        )}
      </div>

      {/* Section 4: 消息记录自动删除 (Auto-Delete History) */}
      <div className="p-4 bg-slate-950/60 border border-slate-800 rounded-xl space-y-3">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={!!batchActionType.autoDeleteTTL}
            onChange={(e) =>
              setBatchActionType({ ...batchActionType, autoDeleteTTL: e.target.checked })
            }
            className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 focus:ring-offset-slate-900"
          />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-sky-500/10 flex items-center justify-center text-sky-400">
              <Clock className="w-3.5 h-3.5" />
            </div>
            <span className="text-xs font-bold text-slate-200">
              批量配置新对话消息自动删除周期
            </span>
          </div>
        </label>

        {batchActionType.autoDeleteTTL && (
          <div className="pl-6 pt-1 space-y-2 text-xs">
            <div className="flex items-center gap-3">
              <span className="text-slate-400 text-xs">新消息自动销毁:</span>
              <select
                value={batchAutoDeleteTTL}
                onChange={(e) => setBatchAutoDeleteTTL(Number(e.target.value))}
                className="bg-slate-900 border border-slate-700 text-sky-300 font-semibold rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-purple-500"
              >
                <option value={0}>🚫 关闭 (永久保留消息)</option>
                <option value={86400}>🕒 1 天 (24 小时后自动焚毁)</option>
                <option value={604800}>📅 1 周 (7 天后自动焚毁)</option>
                <option value={2678400}>🗓️ 1 个月 (30 天后自动焚毁)</option>
              </select>
            </div>
            <p className="text-[11px] text-slate-400">
              应用于账号新创建的私聊和群聊对话，自动按设定周期定时焚毁消息。
            </p>
          </div>
        )}
      </div>

      {/* Section 5: 常用联系人推荐 (Top Peers) */}
      <div className="p-4 bg-slate-950/60 border border-slate-800 rounded-xl space-y-3">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={!!batchActionType.suggestTopContacts}
            onChange={(e) =>
              setBatchActionType({
                ...batchActionType,
                suggestTopContacts: e.target.checked
              })
            }
            className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 focus:ring-offset-slate-900"
          />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400">
              <UserCheck className="w-3.5 h-3.5" />
            </div>
            <span className="text-xs font-bold text-slate-200">
              批量切换推荐常用联系人
            </span>
          </div>
        </label>

        {batchActionType.suggestTopContacts && (
          <div className="pl-6 pt-1 space-y-2 text-xs">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-1.5 cursor-pointer text-slate-300">
                <input
                  type="radio"
                  name="batchSuggestTopContacts"
                  checked={batchSuggestTopContacts === true}
                  onChange={() => setBatchSuggestTopContacts(true)}
                  className="text-purple-600 bg-slate-900 border-slate-700"
                />
                <span className="text-emerald-400 font-semibold">开启置顶推荐</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer text-slate-300">
                <input
                  type="radio"
                  name="batchSuggestTopContacts"
                  checked={batchSuggestTopContacts === false}
                  onChange={() => setBatchSuggestTopContacts(false)}
                  className="text-purple-600 bg-slate-900 border-slate-700"
                />
                <span className="text-slate-400">关闭置顶推荐</span>
              </label>
            </div>
            <p className="text-[11px] text-slate-400">
              在 Telegram 搜索区域顶部展示或隐藏常用联系人。
            </p>
          </div>
        )}
      </div>

      {/* Section 6: 强制注销其他所有活跃会话 (Terminate other sessions) */}
      <div className="p-4 bg-slate-950/60 border border-slate-800 rounded-xl space-y-2">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={!!batchActionType.terminateOtherSessions}
            onChange={(e) =>
              setBatchActionType({
                ...batchActionType,
                terminateOtherSessions: e.target.checked
              })
            }
            className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 focus:ring-offset-slate-900"
          />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-rose-500/10 flex items-center justify-center text-rose-400">
              <Laptop className="w-3.5 h-3.5" />
            </div>
            <span className="text-xs font-bold text-slate-200">
              批量强制注销除当前后台外的所有活跃登录会话
            </span>
          </div>
        </label>
        <p className="pl-6 text-[11px] text-slate-400 leading-relaxed">
          🛡️ <strong className="text-rose-400">防顶号与防盗号神器:</strong> 一键将所有选中账号在外部手机、PC客户端或Web端的异地登录踢下线，仅保留本系统的协议管理权限。
        </p>
      </div>

      {/* Section 7: 清空付款和配送信息 (Clear Payments) */}
      <div className="p-4 bg-slate-950/60 border border-slate-800 rounded-xl space-y-2">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={!!batchActionType.clearPayments}
            onChange={(e) =>
              setBatchActionType({
                ...batchActionType,
                clearPayments: e.target.checked
              })
            }
            className="w-4 h-4 rounded border-slate-700 bg-slate-900 text-purple-600 focus:ring-purple-500 focus:ring-offset-slate-900"
          />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400">
              <CreditCard className="w-3.5 h-3.5" />
            </div>
            <span className="text-xs font-bold text-slate-200">
              批量清空所有已保存的付款和配送信息
            </span>
          </div>
        </label>
        <p className="pl-6 text-[11px] text-slate-400 leading-relaxed">
          一键清除选定账号中 Telegram 绑定的信用卡、密码凭证及收货地址信息。
        </p>
      </div>
    </div>
  );
};
